import React, { useState, useEffect } from 'react';
import { useWizard } from '../../contexts/WizardContext';
import { ethers } from 'ethers';
import { isStellarNetwork } from '../../config/networks';
import { inspectStellarProtectedContract } from '../../services/stellar';
import './Step3ValidationEngine.css';

// Validation Engine ABI - only the function we use for verification
const VALIDATION_ENGINE_ABI = [
	{
		"inputs": [],
		"name": "validationRegistry",
		"outputs": [
			{
				"internalType": "contract ValidatorRegistry",
				"name": "",
				"type": "address"
			}
		],
		"stateMutability": "view",
		"type": "function"
	}
] as const;

const Step3ValidationEngine: React.FC = () => {
  const { currentStep, wizardData, updateWizardData } = useWizard();
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const stellar = isStellarNetwork(wizardData.chain);

  // Re-validate every time we enter step 3 or when chain/contract changes
  useEffect(() => {
    if (wizardData.chain && wizardData.contractAddress) {
      updateWizardData({
        validationEngineProxyAddress: null,
        validationEngineImplementationAddress: null,
        isTrustlineFirewall: false,
        firewallTargetAddress: null,
      });
      setIsVerified(false);
      setError(null);
      fetchValidationEngine();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, wizardData.chain, wizardData.contractAddress]);

  const fetchValidationEngine = async () => {
    if (!wizardData.chain || !wizardData.contractAddress) {
      setError('Chain and contract address are required');
      return;
    }

    setIsFetching(true);
    setError(null);

    try {
      if (isStellarNetwork(wizardData.chain)) {
        const inspection = await inspectStellarProtectedContract(
          wizardData.chain,
          wizardData.contractAddress
        );

        updateWizardData({
          validationEngineProxyAddress: inspection.validationEngineAddress,
          validationEngineImplementationAddress: null,
          isTrustlineFirewall: inspection.isTrustlineFirewall,
          firewallTargetAddress: inspection.firewallTargetAddress,
        });
        setIsVerified(true);
        return;
      }

      updateWizardData({
        isTrustlineFirewall: false,
        firewallTargetAddress: null,
      });

      const provider = new ethers.JsonRpcProvider(wizardData.chain.rpcUrl);

      // Contract ABI to read validationEngine field
      const contractABI = [
        'function validationEngine() view returns (address)',
      ];

      const contract = new ethers.Contract(
        wizardData.contractAddress,
        contractABI,
        provider
      );

      // Fetch validation engine address
      const validationEngineAddress = await contract.validationEngine();

      if (!validationEngineAddress || validationEngineAddress === ethers.ZeroAddress) {
        setError('No validation engine found for this contract');
        setIsFetching(false);
        return;
      }

      // Verify it's a validation engine and check if it's a proxy
      const verificationResult = await verifyValidationEngine(provider, validationEngineAddress);

      if (verificationResult.isValid) {
        updateWizardData({ 
          validationEngineProxyAddress: validationEngineAddress,
          validationEngineImplementationAddress: verificationResult.implementationAddress || null
        });
        setIsVerified(true);
      } else {
        setError(verificationResult.error || 'The fetched address is not a valid Validation Engine');
      }
    } catch (err: any) {
      console.error('Error fetching validation engine:', err);
      setError(err.message || 'Failed to fetch validation engine');
    } finally {
      setIsFetching(false);
    }
  };

  const verifyValidationEngine = async (
    provider: ethers.Provider,
    address: string
  ): Promise<{ isValid: boolean; implementationAddress?: string | null; error?: string }> => {
    try {
      // Check if contract exists
      const code = await provider.getCode(address);
      if (code === '0x') {
        return { isValid: false, error: 'Address does not contain contract code' };
      }

      // Check if it's an ERC1967 proxy by reading the implementation slot
      // ERC1967 implementation slot: keccak256("eip1967.proxy.implementation") - 1
      const implementationSlot = ethers.keccak256(ethers.toUtf8Bytes('eip1967.proxy.implementation'));
      const slotValue = ethers.toBigInt(implementationSlot) - BigInt(1);
      
      const storageValue = await provider.getStorage(address, slotValue);
      
      // If storage slot has a non-zero value, it's a proxy
      if (storageValue && storageValue !== ethers.ZeroHash) {
        // Extract implementation address from storage (last 20 bytes of the 32-byte value)
        // Storage values are padded, so we take the last 20 bytes
        const implementationAddress = ethers.getAddress('0x' + storageValue.slice(-40));
        
        // Verify the implementation address has code
        const implCode = await provider.getCode(implementationAddress);
        if (implCode === '0x') {
          return { 
            isValid: false, 
            error: 'Proxy implementation address does not contain contract code' 
          };
        }

        // Verify the implementation is a validation engine by calling validationRegistry()
        try {
          const implContract = new ethers.Contract(implementationAddress, VALIDATION_ENGINE_ABI, provider);
          // Call validationRegistry() to verify it's a valid validation engine
          // This is a view function specific to validation engines
          await implContract.validationRegistry();
          
          return { 
            isValid: true, 
            implementationAddress 
          };
        } catch (err) {
          console.error('Implementation contract verification error:', err);
          return { 
            isValid: false, 
            error: 'Implementation address is not a valid Validation Engine.' 
          };
        }
      } else {
        // Not a proxy - proxy is mandatory
        return { 
          isValid: false, 
          error: 'A proxy is mandatory. The provided address is not an ERC1967 proxy.' 
        };
      }
    } catch (err: any) {
      console.error('Validation engine verification error:', err);
      return { 
        isValid: false, 
        error: err.message || 'Failed to verify validation engine' 
      };
    }
  };

  const showSuccess =
    isVerified &&
    !!wizardData.validationEngineProxyAddress &&
    (stellar || !!wizardData.validationEngineImplementationAddress);

  return (
    <div className="wizard-step step3-validation-engine">
      <h2 className="wizard-step-title">Trustline Implementation Verification</h2>
      <p className="wizard-step-description">
        Retrieving and verifying details about the Trustline implementation within your contract.
      </p>

      {isFetching ? (
        <div className="wizard-loading">
          Fetching Trustline Implementation Details...
        </div>
      ) : error ? (
        <div className="step3-error-container">
          <div className="wizard-error">{error}</div>
          <button onClick={fetchValidationEngine} className="step3-retry-btn">
            Retry
          </button>
        </div>
      ) : showSuccess ? (
        <div className="step3-success-section">
          <div className="step3-success-icon">✓</div>
          <h3>Trustline Implementation Verified</h3>
          {stellar && wizardData.isTrustlineFirewall && (
            <>
              <p className="step3-address-label">Contract type:</p>
              <p className="step3-address">Trustline Firewall</p>
              <p className="step3-address-label">Firewall address (onboarding contract):</p>
              <p className="step3-address">{wizardData.contractAddress}</p>
              <p className="step3-address-label">Protected target contract:</p>
              <p className="step3-address">{wizardData.firewallTargetAddress}</p>
              <p className="step3-success-message">
                Policy mapping will use the protected target&apos;s on-chain interface (SEP-48), not the firewall&apos;s.
              </p>
            </>
          )}
          <p className="step3-address-label">
            {stellar ? 'Validation Engine Address:' : 'Validation Engine Proxy Address:'}
          </p>
          <p className="step3-address">{wizardData.validationEngineProxyAddress}</p>
          {!stellar && wizardData.validationEngineImplementationAddress && (
            <>
              <p className="step3-address-label">Validation Engine Implementation Address:</p>
              <p className="step3-address">{wizardData.validationEngineImplementationAddress}</p>
            </>
          )}
          <p className="step3-success-message">
            The details of your Trustline implementation have been successfully fetched and verified. Trustline is now ready to be configured.
          </p>
        </div>
      ) : (
        <div className="step3-pending">
          <p>Click the button below to fetch the details of your Trustline implementation.</p>
          <button onClick={fetchValidationEngine} className="step3-fetch-btn">
            Fetch Trustline Implementation Details
          </button>
        </div>
      )}
    </div>
  );
};

export default Step3ValidationEngine;
